import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/ui/BackButton';

const feelingOptions = [
  { value: 'grateful', label: 'Grateful' },
  { value: 'peaceful', label: 'Peaceful' },
  { value: 'hopeful', label: 'Hopeful' },
  { value: 'reflective', label: 'Reflective' },
  { value: 'joyful', label: 'Joyful' },
  { value: 'content', label: 'Content' },
  { value: 'struggling', label: 'Struggling' },
  { value: 'anxious', label: 'Anxious' },
  { value: 'tired', label: 'Tired' },
  { value: 'motivated', label: 'Motivated' },
  { value: 'connected', label: 'Connected' },
  { value: 'distant', label: 'Distant' },
  { value: 'yearning', label: 'Yearning' },
  { value: 'inspired', label: 'Inspired' },
  { value: 'repentant', label: 'Repentant' },
];

interface CheckInStepProps {
  onBack: () => void;
  onContinue: (feelings: string[]) => void;
}

export const CheckInStep = ({ onBack, onContinue }: CheckInStepProps) => {
  const [selectedFeelings, setSelectedFeelings] = useState<string[]>([]);

  const toggleFeeling = (feeling: string) => {
    setSelectedFeelings(prev =>
      prev.includes(feeling)
        ? prev.filter(f => f !== feeling)
        : [...prev, feeling]
    );
  };

  const handleContinue = () => {
    onContinue(selectedFeelings);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-paper flex flex-col"
    >
      {/* Header */}
      <div className="px-4 pt-6 md:px-8 md:pt-8">
        <BackButton onClick={onBack} srLabel="Return to journal" />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col items-center w-full"
        >
          <h1 className="font-display text-2xl text-foreground mb-12">
            How is your heart today?
          </h1>

          {/* Feeling Options */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 gap-4 md:gap-x-6 md:gap-y-4 w-full max-w-[1000px] mx-auto md:[grid-template-columns:repeat(5,minmax(160px,1fr))]"
          >
            {feelingOptions.map((option, index) => {
              const isSelected = selectedFeelings.includes(option.value);
              const isInspired = option.value === 'inspired';
              return (
                <motion.button
                  key={option.value}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ 
                    opacity: 1, 
                    scale: isSelected ? 1.05 : 1,
                  }}
                  whileHover={{ y: -3, scale: 1.03 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ 
                    delay: index * 0.03,
                    scale: { type: 'spring', stiffness: 400, damping: 20 },
                    y: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
                  }}
                  onClick={() => toggleFeeling(option.value)}
                  style={{ minWidth: 160 }}
                  className={`${isInspired ? 'hidden md:flex' : 'flex'} emotion-pill ${isSelected ? 'emotion-pill-selected' : ''} items-center justify-center w-auto px-8 py-3.5 rounded-full text-[0.9rem] font-medium tracking-[0.01em] whitespace-nowrap overflow-visible motion-reduce:transform-none motion-reduce:transition-none ${
                    isSelected
                      ? 'text-foreground'
                      : 'bg-background/80 text-foreground/80'
                  }`}
                >
                  {option.label}
                </motion.button>
              );
            })}
          </motion.div>

          <div className="h-12" />

          {/* Continue Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Button
              onClick={handleContinue}
              className="gap-2 px-8"
              variant="outline"
            >
              Continue to Reflection
              <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};
